import { Link } from 'react-router-dom';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Disclosure } from '../components/ui.tsx';
import { Ring } from '../components/Ring.tsx';
import { EncryptionScopeNotice } from '../components/Disclosures.tsx';
import { useT, type Translate } from '../i18n/I18n.tsx';
import type { MessageKey } from '../i18n/messages/en.js';

/**
 * The public landing page.
 *
 * Every claim here is one the build can actually keep. "We can't read it" is
 * qualified in the same breath, and what the product is *not* sits on the page
 * rather than in a footer — a privacy claim that has to be chased is not a
 * claim, it is marketing.
 *
 * The page is a stack of full-bleed bands, one idea each, alternating white and
 * a soft tint, with the content inside every band held to a single centred
 * measure. See the "Public / marketing surfaces" section of styles.css.
 */

const FLOW: Array<{ icon: IconName; title: MessageKey; body: MessageKey }> = [
  { icon: 'link', title: 'landing.step1Title', body: 'landing.step1Body' },
  { icon: 'user', title: 'landing.step2Title', body: 'landing.step2Body' },
  { icon: 'clock', title: 'landing.step3Title', body: 'landing.step3Body' },
];

const FEATURES: Array<{ icon: IconName; title: MessageKey; body: MessageKey }> = [
  { icon: 'link', title: 'landing.feature1Title', body: 'landing.feature1Body' },
  { icon: 'user', title: 'landing.feature2Title', body: 'landing.feature2Body' },
  { icon: 'clock', title: 'landing.feature3Title', body: 'landing.feature3Body' },
  { icon: 'lock', title: 'landing.feature4Title', body: 'landing.feature4Body' },
  { icon: 'eye-off', title: 'landing.feature5Title', body: 'landing.feature5Body' },
];

const CARRIES: Array<{ icon: IconName; label: MessageKey }> = [
  { icon: 'message', label: 'landing.carriesMessages' },
  { icon: 'image', label: 'landing.carriesPhotos' },
  { icon: 'video', label: 'landing.carriesVideos' },
  { icon: 'file', label: 'landing.carriesFiles' },
  { icon: 'mic', label: 'landing.carriesVoice' },
];

export function Landing() {
  const t: Translate = useT();

  return (
    <div className="landing">
      <section className="lsec hero">
        <div className="lsec__inner">
          <div className="hero__lead">
            {/* The brand's core message (docs/brand.md §7), one line per claim. */}
            <h1>
              <span className="is-accent">{t('landing.heroLine1')}</span>
              <span className="is-accent">{t('landing.heroLine2')}</span>
              <span>{t('landing.heroLine3')}</span>
            </h1>
            <p>{t('landing.heroBody')}</p>
            <div className="hero__actions">
              <Link className="btn btn--primary btn--lg" to="/login">
                <Icon name="mail" />
                {t('landing.signIn')}
              </Link>
              <span className="pill">
                <Icon name="lock" size={14} />
                {t('landing.noAccount')}
              </span>
            </div>
          </div>

          {/* The page's product shot: the clock the whole thing runs on. */}
          <div className="hero__stage">
            <Ring value="24h" label={t('landing.ringLabel')} fraction={1} size={260} />
          </div>
        </div>
      </section>

      <section className="lsec lsec--tinted">
        <div className="lsec__inner">
          <h2 className="section-title">{t('landing.howTitle')}</h2>
          <ol className="flow">
            {FLOW.map((step, index) => (
              <li className="flow__step" key={step.title}>
                <span className="tile tile--lg tile--accent" aria-hidden="true">
                  <Icon name={step.icon} size={24} />
                </span>
                <h3 className="flow__title">
                  {index + 1}. {t(step.title)}
                </h3>
                <p className="flow__body">{t(step.body)}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="lsec">
        <div className="lsec__inner">
          <h2 className="section-title">{t('landing.getTitle')}</h2>
          <ul className="features">
            {FEATURES.map((feature) => (
              <li className="feature" key={feature.title}>
                <span className="tile" aria-hidden="true">
                  <Icon name={feature.icon} size={20} />
                </span>
                <span className="feature__text">
                  <h3 className="feature__title">{t(feature.title)}</h3>
                  <p className="feature__body">{t(feature.body)}</p>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="lsec lsec--tinted">
        <div className="lsec__inner">
          <h2 className="section-title">{t('landing.carriesTitle')}</h2>
          <ul className="carries">
            {CARRIES.map((item) => (
              <li key={item.label}>
                <span className="tile tile--sm" aria-hidden="true">
                  <Icon name={item.icon} size={17} />
                </span>
                {t(item.label)}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="lsec">
        <div className="lsec__inner">
          <div className="landing__close">
            <EncryptionScopeNotice />
            <Disclosure summary={t('landing.notTitle')} icon="info">
              <p className="text-sm muted">{t('landing.notBody1')}</p>
              <p className="text-sm muted">{t('landing.notBody2')}</p>
            </Disclosure>
          </div>
        </div>
      </section>
    </div>
  );
}
